import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useCurrency } from '../../contexts/CompanyContext';
import { useDraftAutosave } from '../../hooks/useDraftAutosave';
import { DraftRestoreBanner, DraftSavedIndicator } from '../../components/DraftBanner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  Plus, 
  CircleNotch,
  Trash,
  ArrowLeft,
  CheckCircle,
  XCircle,
  FileText,
  PencilSimple,
  Warning,
  Eye,
  MagnifyingGlass,
  FilePdf,
  FileXls,
  TrendUp,
  TrendDown,
  ArrowsLeftRight,
  Coins,
} from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';
import RowActionsMenu from '../../components/common/RowActionsMenu';
import { Link } from 'react-router-dom';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  draft: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',
  posted: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20',
  void: 'bg-zinc-500/10 text-zinc-600 dark:text-zinc-400 border-zinc-500/20',
};

// Color scheme + icon per derived entry type
const typeStyles = {
  Income:   { badge: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30', bar: 'border-l-4 border-l-emerald-500/60', icon: TrendUp,         tint: 'text-emerald-600' },
  Expense:  { badge: 'bg-rose-500/10 text-rose-700 dark:text-rose-300 border-rose-500/30',           bar: 'border-l-4 border-l-rose-500/60',     icon: TrendDown,       tint: 'text-rose-600' },
  Transfer: { badge: 'bg-sky-500/10 text-sky-700 dark:text-sky-300 border-sky-500/30',                bar: 'border-l-4 border-l-sky-500/60',      icon: ArrowsLeftRight, tint: 'text-sky-600' },
  Other:    { badge: 'bg-zinc-500/10 text-zinc-700 dark:text-zinc-300 border-zinc-500/30',           bar: 'border-l-4 border-l-zinc-400/50',     icon: Coins,           tint: 'text-zinc-600' },
};

/**
 * Classify a journal entry.
 * Priority 1 — JE.source ("payment_voucher" -> Expense, "receipt_voucher" -> Income).
 * Priority 2 — account-type heuristic on the lines (matches backend `_classify_entry_type`).
 */
function classifyEntryType(entry, accountsMap) {
  const src = (entry?.source || '').toLowerCase();
  if (src === 'payment_voucher') return 'Expense';
  if (src === 'receipt_voucher') return 'Income';
  const lines = entry?.lines || [];
  let hasIncomeCredit = false;
  let hasExpenseDebit = false;
  let onlyBalanceSheet = true;
  let anyAccount = false;
  for (const ln of lines) {
    const acc = accountsMap[ln.account_id];
    const atype = (acc?.account_type || '').toLowerCase();
    if (atype) anyAccount = true;
    const d = parseFloat(ln.debit) || 0;
    const c = parseFloat(ln.credit) || 0;
    if (atype === 'income' && c > 0) hasIncomeCredit = true;
    if (atype === 'expense' && d > 0) hasExpenseDebit = true;
    if (atype && atype !== 'asset' && atype !== 'liability') onlyBalanceSheet = false;
  }
  if (hasIncomeCredit) return 'Income';
  if (hasExpenseDebit) return 'Expense';
  if (onlyBalanceSheet && anyAccount) return 'Transfer';
  return 'Other';
}

export default function JournalEntriesPage() {
  const { hasPermission, user } = useAuth();
  const { formatCurrency } = useCurrency();
  const [entries, setEntries] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  
  const [newEntry, setNewEntry] = useState({
    date: new Date().toISOString().split('T')[0],
    reference: '',
    description: '',
    lines: [
      { account_id: '', debit: '', credit: '', description: '' },
      { account_id: '', debit: '', credit: '', description: '' }
    ]
  });

  // Draft auto-save for new journal entry creation
  const journalDraft = useDraftAutosave({
    key: 'journal-entry',
    data: newEntry,
    enabled: isCreateOpen,
    userId: user?.id,
  });

  const canCreate = hasPermission('accounts', 'create');
  const canEdit = hasPermission('accounts', 'edit');
  const canDelete = hasPermission('accounts', 'delete');
  
  // Edit/Delete states
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState(null);

  // View details modal state
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [viewEntry, setViewEntry] = useState(null);

  // Filters / search state for the entries list
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterStart, setFilterStart] = useState('');
  const [filterEnd, setFilterEnd] = useState('');

  const accountsMap = useMemo(() => {
    const m = {};
    accounts.forEach(a => { m[a.id] = a; });
    return m;
  }, [accounts]);

  const enrichedEntries = useMemo(() => {
    return (entries || []).map(e => ({ ...e, _type: classifyEntryType(e, accountsMap) }));
  }, [entries, accountsMap]);

  const filteredEntries = useMemo(() => {
    const q = (search || '').trim().toLowerCase();
    return enrichedEntries.filter(e => {
      if (filterStatus !== 'all' && e.status !== filterStatus) return false;
      if (filterType !== 'all' && e._type !== filterType) return false;
      if (filterStart && (e.date || '') < filterStart) return false;
      if (filterEnd && (e.date || '') > filterEnd) return false;
      if (q) {
        const hay = [
          `je-${String(e.entry_number || '').padStart(4, '0')}`,
          e.reference || '',
          e.description || '',
        ].join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [enrichedEntries, search, filterStatus, filterType, filterStart, filterEnd]);

  const summary = useMemo(() => {
    const s = { count: filteredEntries.length, debit: 0, credit: 0, posted: 0, draft: 0, voided: 0, income: 0, expense: 0, transfer: 0, other: 0 };
    for (const e of filteredEntries) {
      s.debit += parseFloat(e.total_debit) || 0;
      s.credit += parseFloat(e.total_credit) || 0;
      if (e.status === 'posted') s.posted += 1;
      else if (e.status === 'draft') s.draft += 1;
      else if (e.status === 'void') s.voided += 1;
      const t = (e._type || 'Other').toLowerCase();
      if (t === 'income') s.income += 1;
      else if (t === 'expense') s.expense += 1;
      else if (t === 'transfer') s.transfer += 1;
      else s.other += 1;
    }
    return s;
  }, [filteredEntries]);

  const handleExport = async (fmt) => {
    try {
      const token =
        localStorage.getItem('erp_token') ||
        localStorage.getItem('access_token') ||
        localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filterStart) params.set('start_date', filterStart);
      if (filterEnd) params.set('end_date', filterEnd);
      if (filterStatus !== 'all') params.set('entry_status', filterStatus);
      if (filterType !== 'all') params.set('entry_type', filterType);
      if (search.trim()) params.set('q', search.trim());
      const url = `${API_URL}/reports/journal-entries.${fmt}?${params.toString()}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        throw new Error(txt || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `journal-entries.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      toast.error(`Export failed: ${e.message || 'unknown error'}`);
    }
  };

  const accountById = (id) => accounts.find(a => a.id === id);

  const handleViewEntry = async (entry) => {
    try {
      const res = await axios.get(`${API_URL}/journal-entries/${entry.id}`);
      setViewEntry(res.data);
      setIsViewOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.message || error.response?.data?.detail || 'Failed to load entry details');
    }
  };

  const handleForceDeleteEntry = async (entry) => {
    if (!window.confirm(
      `⚠️ FORCE DELETE posted journal entry?\n\n` +
      `JE-${String(entry.entry_number).padStart(4, '0')} · ${entry.reference || 'No ref'} · ` +
      `DR ${entry.total_debit} / CR ${entry.total_credit}\n\n` +
      `This will:\n` +
      `• Permanently remove this entry from your books\n` +
      `• Reverse its impact on all account balances\n` +
      `• Update Trial Balance, P&L, and reports immediately\n\n` +
      `Use this only to clean up test data or legacy mistakes.\n` +
      `Continue?`
    )) return;
    try {
      await axios.delete(`${API_URL}/journal-entries/${entry.id}?force=true`);
      toast.success('Journal entry permanently deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || error.response?.data?.detail || 'Force delete failed');
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus]);

  const fetchData = async () => {
    try {
      // Ask the backend to include void entries whenever the user has
      // selected "All" or "Void" — otherwise voided rows are hidden so the
      // list totals reflect only live accounting activity.
      const includeVoided = filterStatus === 'all' || filterStatus === 'void';
      const [entriesRes, accountsRes] = await Promise.all([
        axios.get(`${API_URL}/journal-entries?include_voided=${includeVoided}`),
        axios.get(`${API_URL}/accounts`)
      ]);
      setEntries(entriesRes.data);
      setAccounts(accountsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddLine = () => {
    setNewEntry(prev => ({
      ...prev,
      lines: [...prev.lines, { account_id: '', debit: '', credit: '', description: '' }]
    }));
  };

  const handleRemoveLine = (index) => {
    if (newEntry.lines.length <= 2) {
      toast.error('Minimum 2 lines required');
      return;
    }
    setNewEntry(prev => ({
      ...prev,
      lines: prev.lines.filter((_, i) => i !== index)
    }));
  };

  const handleLineChange = (index, field, value) => {
    setNewEntry(prev => ({
      ...prev,
      lines: prev.lines.map((line, i) => 
        i === index ? { ...line, [field]: value } : line
      )
    }));
  };

  const calculateTotals = () => {
    const totalDebit = newEntry.lines.reduce((sum, line) => sum + (parseFloat(line.debit) || 0), 0);
    const totalCredit = newEntry.lines.reduce((sum, line) => sum + (parseFloat(line.credit) || 0), 0);
    return { totalDebit, totalCredit, isBalanced: Math.abs(totalDebit - totalCredit) < 0.01 };
  };

  const handleCreateEntry = async (e) => {
    e.preventDefault();
    const { totalDebit, totalCredit, isBalanced } = calculateTotals();
    
    if (!isBalanced) {
      toast.error(`Entry must be balanced. Debit: ${totalDebit.toFixed(2)}, Credit: ${totalCredit.toFixed(2)}`);
      return;
    }
    
    setFormLoading(true);
    try {
      const payload = {
        ...newEntry,
        lines: newEntry.lines.map(line => ({
          account_id: line.account_id,
          debit: parseFloat(line.debit) || 0,
          credit: parseFloat(line.credit) || 0,
          description: line.description || null
        })).filter(line => line.debit > 0 || line.credit > 0)
      };
      
      await axios.post(`${API_URL}/journal-entries`, payload);
      toast.success('Journal entry created successfully');
      setIsCreateOpen(false);
      journalDraft.clear();
      setNewEntry({
        date: new Date().toISOString().split('T')[0],
        reference: '',
        description: '',
        lines: [
          { account_id: '', debit: '', credit: '', description: '' },
          { account_id: '', debit: '', credit: '', description: '' }
        ]
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create entry');
    } finally {
      setFormLoading(false);
    }
  };

  const handlePostEntry = async (entryId) => {
    try {
      await axios.post(`${API_URL}/journal-entries/${entryId}/post`);
      toast.success('Entry posted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post entry');
    }
  };

  const handleVoidEntry = async (entryId) => {
    if (!window.confirm('Are you sure you want to void this entry?')) return;
    try {
      await axios.post(`${API_URL}/journal-entries/${entryId}/void`);
      toast.success('Entry voided successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to void entry');
    }
  };

  // Edit entry handler - load full entry data
  const handleEditEntry = async (entry) => {
    try {
      const res = await axios.get(`${API_URL}/journal-entries/${entry.id}`);
      const fullEntry = res.data;
      setEditEntry({
        id: fullEntry.id,
        date: fullEntry.date,
        reference: fullEntry.reference || '',
        description: fullEntry.description,
        lines: fullEntry.lines.map(line => ({
          account_id: line.account_id,
          debit: line.debit > 0 ? line.debit.toString() : '',
          credit: line.credit > 0 ? line.credit.toString() : '',
          description: line.description || ''
        }))
      });
      setIsEditOpen(true);
    } catch (error) {
      toast.error('Failed to load entry details');
    }
  };

  const handleEditLineChange = (index, field, value) => {
    setEditEntry(prev => ({
      ...prev,
      lines: prev.lines.map((line, i) => 
        i === index ? { ...line, [field]: value } : line
      )
    }));
  };

  const handleEditAddLine = () => {
    setEditEntry(prev => ({
      ...prev,
      lines: [...prev.lines, { account_id: '', debit: '', credit: '', description: '' }]
    }));
  };

  const handleEditRemoveLine = (index) => {
    if (editEntry.lines.length <= 2) {
      toast.error('Minimum 2 lines required');
      return;
    }
    setEditEntry(prev => ({
      ...prev,
      lines: prev.lines.filter((_, i) => i !== index)
    }));
  };

  const calculateEditTotals = () => {
    if (!editEntry) return { totalDebit: 0, totalCredit: 0, isBalanced: false };
    const totalDebit = editEntry.lines.reduce((sum, line) => sum + (parseFloat(line.debit) || 0), 0);
    const totalCredit = editEntry.lines.reduce((sum, line) => sum + (parseFloat(line.credit) || 0), 0);
    return { totalDebit, totalCredit, isBalanced: Math.abs(totalDebit - totalCredit) < 0.01 };
  };

  const handleUpdateEntry = async (e) => {
    e.preventDefault();
    const { totalDebit, totalCredit, isBalanced } = calculateEditTotals();
    
    if (!isBalanced) {
      toast.error(`Entry must be balanced. Debit: ${totalDebit.toFixed(2)}, Credit: ${totalCredit.toFixed(2)}`);
      return;
    }
    
    setFormLoading(true);
    try {
      const payload = {
        date: editEntry.date,
        reference: editEntry.reference,
        description: editEntry.description,
        lines: editEntry.lines.map(line => ({
          account_id: line.account_id,
          debit: parseFloat(line.debit) || 0,
          credit: parseFloat(line.credit) || 0,
          description: line.description || null
        })).filter(line => line.debit > 0 || line.credit > 0)
      };
      
      await axios.put(`${API_URL}/journal-entries/${editEntry.id}`, payload);
      toast.success('Journal entry updated successfully');
      setIsEditOpen(false);
      setEditEntry(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update entry');
    } finally {
      setFormLoading(false);
    }
  };

  // Delete entry handler
  const handleDeleteClick = (entry) => {
    setEntryToDelete(entry);
    setShowDeleteConfirm(true);
  };

  const handleDeleteEntry = async () => {
    if (!entryToDelete) return;
    setFormLoading(true);
    try {
      await axios.delete(`${API_URL}/journal-entries/${entryToDelete.id}`);
      toast.success('Journal entry deleted successfully');
      setShowDeleteConfirm(false);
      setEntryToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete entry');
    } finally {
      setFormLoading(false);
    }
  };

  const { totalDebit, totalCredit, isBalanced } = calculateTotals();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <CircleNotch size={32} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="journal-entries-page">
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div className="flex items-center gap-4">
          <Link to="/dashboard/accounts">
            <Button variant="ghost" size="icon">
              <ArrowLeft size={20} />
            </Button>
          </Link>
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-semibold">
              Journal Entries
            </h1>
            <p className="text-muted-foreground text-sm sm:text-base leading-relaxed mt-1">
              Record and manage double-entry accounting transactions
            </p>
          </div>
        </div>
        {canCreate && accounts.length > 0 && (
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-entry-btn">
                <Plus size={18} className="mr-2" />
                New Entry
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create Journal Entry</DialogTitle>
                <DialogDescription>
                  Record a new double-entry accounting transaction
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateEntry} className="space-y-4">
                <DraftRestoreBanner
                  visible={journalDraft.hasDraft}
                  savedAt={journalDraft.draftSavedAt}
                  label="journal entry"
                  onRestore={() => { const r = journalDraft.restore(); if (r) setNewEntry(r); }}
                  onDiscard={journalDraft.discard}
                />
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Date</Label>
                    <Input
                      type="date"
                      value={newEntry.date}
                      onChange={(e) => setNewEntry(p => ({ ...p, date: e.target.value }))}
                      required
                      data-testid="entry-date"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Reference (Optional)</Label>
                    <Input
                      value={newEntry.reference}
                      onChange={(e) => setNewEntry(p => ({ ...p, reference: e.target.value }))}
                      placeholder="INV-001"
                      data-testid="entry-reference"
                    />
                  </div>
                  <div className="space-y-2 col-span-1">
                    <Label>Description</Label>
                    <Input
                      value={newEntry.description}
                      onChange={(e) => setNewEntry(p => ({ ...p, description: e.target.value }))}
                      placeholder="Transaction description"
                      required
                      data-testid="entry-description"
                    />
                  </div>
                </div>

                {/* Line Items */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Line Items</Label>
                    <Button type="button" variant="outline" size="sm" onClick={handleAddLine}>
                      <Plus size={14} className="mr-1" /> Add Line
                    </Button>
                  </div>
                  
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="text-left px-3 py-2 text-xs font-medium">Account</th>
                          <th className="text-right px-3 py-2 text-xs font-medium w-28">Debit</th>
                          <th className="text-right px-3 py-2 text-xs font-medium w-28">Credit</th>
                          <th className="w-10"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {newEntry.lines.map((line, index) => (
                          <tr key={index}>
                            <td className="px-3 py-2">
                              <Select 
                                value={line.account_id} 
                                onValueChange={(v) => handleLineChange(index, 'account_id', v)}
                              >
                                <SelectTrigger data-testid={`line-${index}-account`}>
                                  <SelectValue placeholder="Select account" />
                                </SelectTrigger>
                                <SelectContent>
                                  {accounts.map(acc => (
                                    <SelectItem key={acc.id} value={acc.id}>
                                      {acc.code} - {acc.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="px-3 py-2">
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={line.debit}
                                onChange={(e) => handleLineChange(index, 'debit', e.target.value)}
                                placeholder="0.00"
                                className="text-right"
                                disabled={parseFloat(line.credit) > 0}
                                data-testid={`line-${index}-debit`}
                              />
                            </td>
                            <td className="px-3 py-2">
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={line.credit}
                                onChange={(e) => handleLineChange(index, 'credit', e.target.value)}
                                placeholder="0.00"
                                className="text-right"
                                disabled={parseFloat(line.debit) > 0}
                                data-testid={`line-${index}-credit`}
                              />
                            </td>
                            <td className="px-3 py-2">
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => handleRemoveLine(index)}
                                className="h-8 w-8"
                              >
                                <Trash size={14} />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-muted/30">
                        <tr>
                          <td className="px-3 py-2 font-medium">Totals</td>
                          <td className="px-3 py-2 text-right font-mono font-medium">
                            {formatCurrency(totalDebit)}
                          </td>
                          <td className="px-3 py-2 text-right font-mono font-medium">
                            {formatCurrency(totalCredit)}
                          </td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  
                  {/* Balance indicator */}
                  <div className={`flex items-center gap-2 text-sm ${isBalanced ? 'text-emerald-600' : 'text-red-600'}`}>
                    {isBalanced ? (
                      <>
                        <CheckCircle size={16} weight="fill" />
                        <span>Entry is balanced</span>
                      </>
                    ) : (
                      <>
                        <XCircle size={16} weight="fill" />
                        <span>Difference: {formatCurrency(Math.abs(totalDebit - totalCredit))}</span>
                      </>
                    )}
                  </div>
                </div>

                <DialogFooter>
                  <div className="mr-auto"><DraftSavedIndicator savedAt={journalDraft.lastAutoSavedAt} /></div>
                  <Button type="submit" disabled={formLoading || !isBalanced} data-testid="create-entry-submit">
                    {formLoading ? <CircleNotch className="animate-spin" size={18} /> : 'Create Entry'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </motion.div>

      {/* KPI Summary Strip */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 }}
        className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3"
        data-testid="journal-summary-strip"
      >
        <div className="rounded-lg border bg-card px-4 py-3">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Entries</p>
          <p className="text-2xl font-semibold mt-1" data-testid="summary-count">{summary.count}</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">{summary.posted} posted · {summary.draft} draft · {summary.voided} void</p>
        </div>
        <div className="rounded-lg border bg-card px-4 py-3">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Total Debit</p>
          <p className="text-2xl font-semibold mt-1 font-mono" data-testid="summary-debit">{formatCurrency(summary.debit)}</p>
        </div>
        <div className="rounded-lg border bg-card px-4 py-3">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Total Credit</p>
          <p className="text-2xl font-semibold mt-1 font-mono" data-testid="summary-credit">{formatCurrency(summary.credit)}</p>
        </div>
        <div className="rounded-lg border bg-card px-4 py-3 border-l-4 border-l-emerald-500/60">
          <p className="text-[10px] uppercase tracking-wide text-emerald-700 dark:text-emerald-300 flex items-center gap-1"><TrendUp size={11} /> Income</p>
          <p className="text-2xl font-semibold mt-1" data-testid="summary-income">{summary.income}</p>
        </div>
        <div className="rounded-lg border bg-card px-4 py-3 border-l-4 border-l-rose-500/60">
          <p className="text-[10px] uppercase tracking-wide text-rose-700 dark:text-rose-300 flex items-center gap-1"><TrendDown size={11} /> Expense</p>
          <p className="text-2xl font-semibold mt-1" data-testid="summary-expense">{summary.expense}</p>
        </div>
        <div className="rounded-lg border bg-card px-4 py-3 border-l-4 border-l-sky-500/60">
          <p className="text-[10px] uppercase tracking-wide text-sky-700 dark:text-sky-300 flex items-center gap-1"><ArrowsLeftRight size={11} /> Transfer</p>
          <p className="text-2xl font-semibold mt-1" data-testid="summary-transfer">{summary.transfer}</p>
        </div>
      </motion.div>

      {/* Filter / Search Toolbar */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.08 }}
        className="flex flex-wrap items-end gap-2 p-3 rounded-lg border bg-muted/20"
        data-testid="journal-toolbar"
      >
        <div className="flex-1 min-w-[200px]">
          <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Search</Label>
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Entry #, reference or description…"
              className="pl-7 h-9"
              data-testid="journal-search"
            />
          </div>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">From</Label>
          <Input
            type="date"
            value={filterStart}
            onChange={(e) => setFilterStart(e.target.value)}
            className="h-9 w-40"
            data-testid="journal-filter-start"
          />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">To</Label>
          <Input
            type="date"
            value={filterEnd}
            onChange={(e) => setFilterEnd(e.target.value)}
            className="h-9 w-40"
            data-testid="journal-filter-end"
          />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Status</Label>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="h-9 w-36" data-testid="journal-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="posted">Posted</SelectItem>
              <SelectItem value="void">Void</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Type</Label>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="h-9 w-36" data-testid="journal-filter-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="Income">Income</SelectItem>
              <SelectItem value="Expense">Expense</SelectItem>
              <SelectItem value="Transfer">Transfer</SelectItem>
              <SelectItem value="Other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end gap-1.5">
          <Button size="sm" variant="outline" className="h-9" onClick={() => handleExport('pdf')} data-testid="journal-export-pdf">
            <FilePdf size={14} className="mr-1.5" /> PDF
          </Button>
          <Button size="sm" variant="outline" className="h-9" onClick={() => handleExport('xlsx')} data-testid="journal-export-xlsx">
            <FileXls size={14} className="mr-1.5" /> Excel
          </Button>
          {(search || filterStatus !== 'all' || filterType !== 'all' || filterStart || filterEnd) && (
            <Button
              size="sm" variant="ghost" className="h-9"
              onClick={() => { setSearch(''); setFilterStatus('all'); setFilterType('all'); setFilterStart(''); setFilterEnd(''); }}
              data-testid="journal-clear-filters"
            >
              Clear
            </Button>
          )}
        </div>
      </motion.div>

      {/* Entries Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="card-tactile rounded-lg overflow-hidden"
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left px-6 py-4 text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">Entry #</th>
                <th className="text-left px-6 py-4 text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">Date</th>
                <th className="text-left px-6 py-4 text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">Type</th>
                <th className="text-left px-6 py-4 text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">Description</th>
                <th className="text-right px-6 py-4 text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">Debit</th>
                <th className="text-right px-6 py-4 text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">Credit</th>
                <th className="text-left px-6 py-4 text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">Status</th>
                <th className="text-right px-6 py-4 text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredEntries.map((entry) => {
                const ts = typeStyles[entry._type] || typeStyles.Other;
                const TIcon = ts.icon;
                return (
                <tr
                  key={entry.id}
                  className="hover:bg-muted/30 transition-colors cursor-pointer"
                  data-testid={`entry-row-${entry.id}`}
                  data-entry-type={entry._type}
                  onClick={() => handleViewEntry(entry)}
                >
                  <td className={`px-6 py-4 font-mono text-sm ${ts.bar}`}>JE-{String(entry.entry_number).padStart(4, '0')}</td>
                  <td className="px-6 py-4">{entry.date}</td>
                  <td className="px-6 py-4">
                    <Badge variant="outline" className={`${ts.badge} gap-1`}>
                      <TIcon size={11} weight="bold" /> {entry._type}
                    </Badge>
                  </td>
                  <td className="px-6 py-4">
                    <p className="font-medium">{entry.description}</p>
                    {entry.reference && (
                      <p className="text-xs text-muted-foreground">Ref: {entry.reference}</p>
                    )}
                  </td>
                  <td className={`px-6 py-4 text-right font-mono ${ts.tint}`}>{formatCurrency(entry.total_debit)}</td>
                  <td className={`px-6 py-4 text-right font-mono ${ts.tint}`}>{formatCurrency(entry.total_credit)}</td>
                  <td className="px-6 py-4">
                    <Badge variant="outline" className={statusColors[entry.status]}>
                      {entry.status.charAt(0).toUpperCase() + entry.status.slice(1)}
                    </Badge>
                  </td>
                  <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex justify-end">
                      <RowActionsMenu
                        testid={`entry-${entry.id}`}
                        actions={[
                          { id: 'view', label: 'View', icon: Eye, onClick: () => handleViewEntry(entry) },
                          entry.status === 'draft' && canEdit && { id: 'edit', label: 'Edit', icon: PencilSimple, onClick: () => handleEditEntry(entry) },
                          entry.status === 'draft' && canEdit && { id: 'post', label: 'Post', icon: CheckCircle, onClick: () => handlePostEntry(entry.id) },
                          entry.status === 'draft' && canEdit && canDelete && 'separator',
                          entry.status === 'draft' && canEdit && canDelete && { id: 'delete', label: 'Delete Draft', icon: Trash, onClick: () => handleDeleteClick(entry), destructive: true },
                          entry.status === 'posted' && canEdit && { id: 'void', label: 'Void (Reverse)', onClick: () => handleVoidEntry(entry.id) },
                          entry.status === 'posted' && canEdit && canDelete && { id: 'force-delete', label: 'Force Delete', icon: Trash, onClick: () => handleForceDeleteEntry(entry), destructive: true },
                        ].filter(Boolean)}
                      />
                    </div>
                  </td>
                </tr>
              );
              })}
            </tbody>
          </table>
        </div>
        {entries.length === 0 && (
          <div className="text-center py-12">
            <FileText size={48} weight="duotone" className="mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No journal entries yet</p>
            {canCreate && accounts.length > 0 && (
              <Button className="mt-4" onClick={() => setIsCreateOpen(true)}>
                <Plus size={18} className="mr-2" />
                Create First Entry
              </Button>
            )}
          </div>
        )}
        {entries.length > 0 && filteredEntries.length === 0 && (
          <div className="text-center py-12" data-testid="journal-empty-filtered">
            <MagnifyingGlass size={40} weight="duotone" className="mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">No entries match the current search / filters.</p>
            <Button
              variant="ghost"
              className="mt-3"
              onClick={() => { setSearch(''); setFilterStatus('all'); setFilterType('all'); setFilterStart(''); setFilterEnd(''); }}
            >
              Clear filters
            </Button>
          </div>
        )}
      </motion.div>

      {/* View Details Modal */}
      <Dialog open={isViewOpen} onOpenChange={setIsViewOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Journal Entry Details · JE-{String(viewEntry?.entry_number || '').padStart(4, '0')}
            </DialogTitle>
            <DialogDescription>
              {viewEntry?.date} · {viewEntry?.reference || 'No reference'} · {viewEntry?.status?.toUpperCase()}
            </DialogDescription>
          </DialogHeader>
          {viewEntry && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Date</p>
                  <p className="font-medium">{viewEntry.date}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Reference</p>
                  <p className="font-medium">{viewEntry.reference || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Status</p>
                  <Badge variant="outline" className={statusColors[viewEntry.status]}>
                    {viewEntry.status?.toUpperCase()}
                  </Badge>
                </div>
                <div className="col-span-3">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Description</p>
                  <p className="font-medium">{viewEntry.description}</p>
                </div>
              </div>

              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left px-3 py-2 text-xs font-medium uppercase tracking-wide">Account</th>
                      <th className="text-left px-3 py-2 text-xs font-medium uppercase tracking-wide">Type</th>
                      <th className="text-left px-3 py-2 text-xs font-medium uppercase tracking-wide">Description</th>
                      <th className="text-right px-3 py-2 text-xs font-medium uppercase tracking-wide w-28">Debit</th>
                      <th className="text-right px-3 py-2 text-xs font-medium uppercase tracking-wide w-28">Credit</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {(viewEntry.lines || []).map((ln, i) => {
                      const acc = accountById(ln.account_id);
                      return (
                        <tr key={i}>
                          <td className="px-3 py-2 font-mono text-sm">
                            {acc ? `${acc.code} — ${acc.name}` : (
                              <span className="text-red-500">⚠️ Orphan ({(ln.account_id || '').slice(0, 8)}…)</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-xs uppercase tracking-wide text-muted-foreground">
                            {acc?.account_type || '—'}
                          </td>
                          <td className="px-3 py-2 text-sm">{ln.description || '—'}</td>
                          <td className="px-3 py-2 text-right font-mono">
                            {ln.debit > 0 ? formatCurrency(ln.debit) : '—'}
                          </td>
                          <td className="px-3 py-2 text-right font-mono">
                            {ln.credit > 0 ? formatCurrency(ln.credit) : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-muted/30 font-semibold">
                    <tr>
                      <td colSpan={3} className="px-3 py-2 text-right">Totals</td>
                      <td className="px-3 py-2 text-right font-mono">{formatCurrency(viewEntry.total_debit)}</td>
                      <td className="px-3 py-2 text-right font-mono">{formatCurrency(viewEntry.total_credit)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {Math.abs((viewEntry.total_debit || 0) - (viewEntry.total_credit || 0)) > 0.01 && (
                <div className="p-3 rounded bg-red-500/10 border border-red-500/20 text-red-600 text-sm">
                  ⚠️ This entry is UNBALANCED — DR ≠ CR. It is corrupting your reports.
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsViewOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Journal Entry Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Journal Entry</DialogTitle>
            <DialogDescription>
              Update journal entry details (draft entries only)
            </DialogDescription>
          </DialogHeader>
          {editEntry && (
            <form onSubmit={handleUpdateEntry} className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Date</Label>
                  <Input
                    type="date"
                    value={editEntry.date}
                    onChange={(e) => setEditEntry(p => ({ ...p, date: e.target.value }))}
                    required
                    data-testid="edit-entry-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Reference (Optional)</Label>
                  <Input
                    value={editEntry.reference}
                    onChange={(e) => setEditEntry(p => ({ ...p, reference: e.target.value }))}
                    placeholder="INV-001"
                    data-testid="edit-entry-reference"
                  />
                </div>
                <div className="space-y-2 col-span-1">
                  <Label>Description</Label>
                  <Input
                    value={editEntry.description}
                    onChange={(e) => setEditEntry(p => ({ ...p, description: e.target.value }))}
                    placeholder="Transaction description"
                    required
                    data-testid="edit-entry-description"
                  />
                </div>
              </div>

              {/* Line Items */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Line Items</Label>
                  <Button type="button" variant="outline" size="sm" onClick={handleEditAddLine}>
                    <Plus size={14} className="mr-1" /> Add Line
                  </Button>
                </div>
                
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left px-3 py-2 text-xs font-medium">Account</th>
                        <th className="text-right px-3 py-2 text-xs font-medium w-28">Debit</th>
                        <th className="text-right px-3 py-2 text-xs font-medium w-28">Credit</th>
                        <th className="w-10"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {editEntry.lines.map((line, index) => (
                        <tr key={index}>
                          <td className="px-3 py-2">
                            <Select 
                              value={line.account_id} 
                              onValueChange={(v) => handleEditLineChange(index, 'account_id', v)}
                            >
                              <SelectTrigger data-testid={`edit-line-${index}-account`}>
                                <SelectValue placeholder="Select account" />
                              </SelectTrigger>
                              <SelectContent>
                                {accounts.map(acc => (
                                  <SelectItem key={acc.id} value={acc.id}>
                                    {acc.code} - {acc.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-3 py-2">
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={line.debit}
                              onChange={(e) => handleEditLineChange(index, 'debit', e.target.value)}
                              placeholder="0.00"
                              className="text-right"
                              disabled={parseFloat(line.credit) > 0}
                              data-testid={`edit-line-${index}-debit`}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={line.credit}
                              onChange={(e) => handleEditLineChange(index, 'credit', e.target.value)}
                              placeholder="0.00"
                              className="text-right"
                              disabled={parseFloat(line.debit) > 0}
                              data-testid={`edit-line-${index}-credit`}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEditRemoveLine(index)}
                              className="h-8 w-8"
                            >
                              <Trash size={14} />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-muted/30">
                      <tr>
                        <td className="px-3 py-2 font-medium">Totals</td>
                        <td className="px-3 py-2 text-right font-mono font-medium">
                          {formatCurrency(calculateEditTotals().totalDebit)}
                        </td>
                        <td className="px-3 py-2 text-right font-mono font-medium">
                          {formatCurrency(calculateEditTotals().totalCredit)}
                        </td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                
                {/* Balance indicator */}
                <div className={`flex items-center gap-2 text-sm ${calculateEditTotals().isBalanced ? 'text-emerald-600' : 'text-red-600'}`}>
                  {calculateEditTotals().isBalanced ? (
                    <>
                      <CheckCircle size={16} weight="fill" />
                      <span>Entry is balanced</span>
                    </>
                  ) : (
                    <>
                      <XCircle size={16} weight="fill" />
                      <span>Difference: {formatCurrency(Math.abs(calculateEditTotals().totalDebit - calculateEditTotals().totalCredit))}</span>
                    </>
                  )}
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsEditOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={formLoading || !calculateEditTotals().isBalanced} data-testid="update-entry-submit">
                  {formLoading ? <CircleNotch className="animate-spin" size={18} /> : 'Update Entry'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash className="w-5 h-5" />
              Delete Journal Entry
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete journal entry <strong>JE-{String(entryToDelete?.entry_number || '').padStart(4, '0')}</strong>? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="p-3 bg-amber-500/10 rounded-lg text-amber-700 text-sm">
            <Warning size={16} className="inline mr-2" />
            Only draft entries can be deleted. Posted entries should be voided instead.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteEntry}
              disabled={formLoading}
              data-testid="confirm-delete-entry"
            >
              {formLoading ? <CircleNotch className="animate-spin" size={18} /> : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
